import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import Grid from "@mui/material/Grid";
import { TextField, Button, Box, Typography, Chip } from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import { announcementPostSelector, getAnnouncementPosts } from '../../store/slices/announcementPostSlice';
import { announcementSelector, clearAnnouncementState, getAnnouncementById, updateAnnouncement, updateAnnouncementStatus } from "../../store/slices/announcementSlice";
import { camelCaseToTitleCase, ERRORS_MESSAGE, isModelOrAlert, SUCCESS_MESSAGE } from '../../constants';

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      // ['image', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
  },
};

const Announcement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { announcementId } = useParams();
  const { announcement, isAnnouncementStatusUpdating, isUpdatingAnnouncement } = useSelector(announcementSelector);
  const { announcementPosts } = useSelector(announcementPostSelector);
  const isContentAndPostAllowed = announcement?.type ? isModelOrAlert(announcement?.type) : false

  const [announcementData, setAnnouncementData] = useState({
    title: '',
    content: '',
    type: '',
    status: '',
    announcementPosts: []
  })

  const handle = {
    onChangeField: (field, value) => {
      setAnnouncementData({
        ...announcementData,
        [field]: value,
      });
    },
    getAnnouncementHandler: (announcementId) => {
      dispatch(getAnnouncementById({ announcementId }));
    },
    handleSave: () => {
      const payload = {
        announcementId,
        title: announcementData.title,
        ...(isContentAndPostAllowed && {
          content: announcementData.content,
          announcementPosts: announcementData.announcementPosts?.map((post) => post._id) || []
        }),
      };

      dispatch(updateAnnouncement(payload)).then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.dataUpdateError, {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(res?.payload?.data?.status_message || SUCCESS_MESSAGE.dataUpdate, {
            variant: "success",
            autoHideDuration: 5000,
          });
        }
      })
    },
    setAnnouncementLive: (status) => {
      const payload = {
        announcementId,
        status
      }

      dispatch(updateAnnouncementStatus(payload)).then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.dataUpdateError, {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(res?.payload?.data?.status_message || SUCCESS_MESSAGE.dataUpdate, {
            variant: "success",
            autoHideDuration: 5000,
          });
        }
      })
    },
    selectPost: (post) => {
      const updatedSelectedPosts = announcementData.announcementPosts || []
      updatedSelectedPosts.push(post)

      setAnnouncementData({
        ...announcementData,
        announcementPosts: updatedSelectedPosts
      });
    },
    removeSelectedPost: (postId) => {
      const updatedSelectedPosts = announcementData.announcementPosts.filter((p) => p._id !== postId);
      setAnnouncementData({
        ...announcementData,
        announcementPosts: updatedSelectedPosts
      });
    },
    handleOnBack: () => {
      navigate(`/announcements`);
    }
  }


  useEffect(() => {
    if (announcementId) {
      handle.getAnnouncementHandler(announcementId);
    }

    return () => {
      dispatch(clearAnnouncementState());
    };
  }, [announcementId]);

  useEffect(() => {
    if (announcement) {
      if (isContentAndPostAllowed) {
        dispatch(getAnnouncementPosts({ limit: 1000 })); // Get all posts
      }

      setAnnouncementData({
        title: announcement.title,
        type: announcement.type,
        status: announcement.status,
        content: announcement.content,
      });
    }
  }, [announcement]);

  useEffect(() => {
    if (announcement && announcementPosts) {
      const selectedPostIds = announcement?.announcementPosts || []
      const selectedPosts = announcementPosts.docs.filter((post) => selectedPostIds.includes(post._id))
      setAnnouncementData({
        ...announcementData,
        announcementPosts: selectedPosts
      });
    }
  }, [announcementPosts]);

  const announcementStatusText = announcement?.status !== "active" ? "Set Live" : "Set Inactive"

  return (
    <>
      <Grid
        container
        direction="column"
        style={{
          padding: "30px 0px 100px",
        }}
      >
        <Grid
          item
          px={4}
          style={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
            justifyContent: "space-between",
          }}
        >
          <Grid
            item
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
            }}
          >
            <Button
              variant="text"
              sx={{ textTransform: "none", color: "black", ml: "-14px" }}
              onClick={handle.handleOnBack}
            >
              <ArrowBackIcon htmlColor={deepPurple[500]} />
            </Button>

            <Typography
              variant="h1"
              style={{
                fontSize: "24px",
                fontWeight: 800,
                letterSpacing: "-0.5px",
              }}
            >
              Announcement
            </Typography>
            <Chip label={camelCaseToTitleCase(announcement?.type)} />
          </Grid>

          <Grid
            item
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
              marginTop: "10px"
            }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={isAnnouncementStatusUpdating}
              onClick={() => handle.setAnnouncementLive(announcement?.status !== "active" ? "active" : "inactive")}
            >
              {isAnnouncementStatusUpdating ? "Saving..." : announcementStatusText}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={isUpdatingAnnouncement}
              onClick={handle.handleSave}
            >
              {isUpdatingAnnouncement ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>


        <Box px={4} py={1}>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={announcementData.title}
            onChange={(e) => handle.onChangeField("title", e.target.value)}
          />
          {isContentAndPostAllowed && (
            <>
              <ReactQuill
                value={announcementData.content || ""}
                modules={modules}
                onChange={(value) => {
                  handle.onChangeField("content", value)
                }}
              />

              <Typography variant="h6" sx={{ m: '5px 0' }}>
                Select Posts
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  mb: 4,
                  maxHeight: 300,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    overflowY: "auto",
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    All Posts
                  </Typography>
                  {announcementPosts?.docs?.map((post, index) => {
                    const isSelected = announcementData?.announcementPosts?.some((p) => p?._id === post._id);
                    return (
                      <Box
                        key={post?._id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                          ...(post?.status !== "active" && {
                            color: "gray"
                          })
                        }}
                      >
                        <Typography>{index + 1}. {post.title}</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isSelected || post?.status !== "active"}
                          onClick={() => handle.selectPost(post)}
                          sx={{ opacity: isSelected ? 0.4 : 1 }}
                        >
                          Select
                        </Button>
                      </Box>
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    overflowY: "auto",
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                    Selected Posts
                  </Typography>
                  {announcementData?.announcementPosts?.length === 0 && (
                    <Typography color="text.secondary">No posts selected</Typography>
                  )}
                  {announcementData?.announcementPosts?.map((post, index) => (
                    <Box
                      key={post._id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography>{index + 1}. {post.title}</Typography>
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        onClick={() => handle.removeSelectedPost(post._id)}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

        </Box>
      </Grid >
    </>
  );
}

export default Announcement